import { apiErrorMessage, retryTransient, setPartnerAuthToken } from '@agos/api-client'
import {
  useGetPartnerAuthMe,
  useGetPartnerMeFranchises,
  useGetPartnerMeInvoices,
  useGetPartnerMeProfitability,
  usePostPartnerAuthLogin,
} from '@agos/api-client/platform'
import { T, formatCents, formatCount, formatDay, tt } from '@agos/shared'
import { DataTable, StatusBadge, type Column } from '@agos/ui'
import { useMemo, useState } from 'react'

import {
  invoiceStandingColor,
  invoiceStandingLabel,
  invoiceStandingOf,
  type PartnerInvoice,
  outstandingCents,
  partnerInvoicesNewestFirst,
  partnerInvoicesOf,
  emptyNote,
  holdingShareNote,
  inactiveNote,
  packageNote,
  isCharge,
  partnerFranchisesOf,
  partnerProfitabilityOf,
  periodNote,
  lastMonth,
  monthToDate,
  yearToDate,
  payoutNote,
  basisNote,
  shareNote,
} from './partner-portal'

/**
 * The partner portal — a **different person** from a super-admin.
 *
 * This is rendered by `App` before the tower shell mounts, and that placement is the design rather than
 * a convenience. Legacy: *"fully isolated from the super-admin auth flow — it uses its own localStorage
 * token and never touches the super-admin context. Render it before mounting AuthProvider so the
 * super-admin `/auth/me` call doesn't fire on the partner login page."*
 *
 * A partner is somebody the platform pays a share to. They have their own token, their own secret
 * server-side (`PARTNER_JWT_SECRET`) and their own three endpoints — `/partner/auth/login`,
 * `/partner/auth/me`, `/partner/me/*`. Nothing on this page reaches a `/super-admin/*` route, and the
 * token goes to `hk_partner_token` rather than the tower's key.
 *
 * The list this portal is about — `GET /super-admin/partners` — carries real people's names, e-mail
 * addresses, phone numbers and **IBANs**. None of it appears in any fixture in this repository.
 */
/** What a signed-in partner sees: their franchises, their share, and what it came to. */
function PartnerDashboard() {
  /* FB-835, §3.159 - this read carries the partner's own **name**, so a blip left the greeting blank on the screen
     §3.152 had just taught to report its own failed reads. The failure branches stay; this reduces how often they
     fire. */
  const account = useGetPartnerAuthMe({ query: { retry: retryTransient } })
  const links = useGetPartnerMeFranchises()
  /* FB-823, §3.152 - **this screen pays somebody outside the business, so a fabricated nought is the worst thing
     it can say.** `partnerProfitabilityOf(undefined)` reads `row.totals` as `{}` and `parseMoneyToCents(undefined)`
     is 0, so a failed read rendered *Váš podiel za obdobie: 0,00 €* in bold to a partner who is owed money. Per
     read rather than per screen (§9.528), because a partner whose invoices failed should still read their payout. */
  /* FB-824, §3.153 - **both bounds, or the server answers 400.** `PartnerPortalService.profitability` throws
     `badRequest("from + to required")`, the generated `params` is **optional**, and this call passed none - so the
     payout has never rendered a real figure. §9.518's rule for the fourth time, and here the call site is generated,
     so the guard asserts the call rather than the signature. */
  const [range, setRange] = useState(() => yearToDate(new Date()))
  const money = useGetPartnerMeProfitability({ from: range.from, to: range.to })
  // §3.40 - the endpoint that had no caller. Legacy's own portal has an invoices table against it;
  // both of the owner's partners had 6 invoices worth 3 434,16 EUR they could not see.
  const billing = useGetPartnerMeInvoices()

  const franchises = useMemo(() => partnerFranchisesOf(links.data), [links.data])
  const report = useMemo(() => partnerProfitabilityOf(money.data), [money.data])
  const invoices = useMemo(() => partnerInvoicesOf(billing.data), [billing.data])
  // Read once per render rather than per row, so every row is judged against the same instant.
  const now = useMemo(() => new Date(), [])
  const shown = useMemo(() => partnerInvoicesNewestFirst(invoices), [invoices])
  const partner = (account.data as { partner?: { name?: unknown } } | undefined)?.partner

  return (
    <main className="mx-auto max-w-3xl space-y-3 p-4" data-partner-dashboard>
      {/* FB-960, §3.186 - **the way out, which this portal had none of.** Legacy's header carries *Odhlásiť*;
          `setPartnerAuthToken` was called once, on login, and nothing in the app ever passed it `null`. The only
          other *Odhlásiť* is `TowerShell`'s, and this page renders **before** that shell mounts, so a partner
          never saw one. `PartnerTokenService.LIFETIME` is 14 hours, so a partner on a shared machine left a
          working credential behind for the rest of the day - one that reads their payout, their franchises and
          every platform invoice against them.
          The subtitle is legacy's own and earns its line on a screen with no controls: a reader otherwise has to
          infer from the absence of buttons that there is nothing here they can change. */}
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold" data-partner-name>
            {typeof partner?.name === 'string' ? partner.name : ''}
          </p>
          <p className="text-[11px] text-muted-foreground" data-partner-readonly>
            <T>Partner portál - iba na čítanie</T>
          </p>
        </div>
        {/* **A reload rather than a refetch**: clearing the key leaves `session` cached as successful, so the
            login form would not come back until something refetched - and the franchises, the payout and the
            invoices would stay in memory with this partner's figures in them. Assigning the path drops every
            cached payload and is symmetric with the sign-in, which assigns the same one.
            **No confirmation**, unlike `TowerShell`'s: §3.175 makes the Tower ask because a screen there can hold
            unsaved work, and this portal is read-only, so there is nothing to lose. Legacy's does not ask. */}
        <button
          type="button"
          data-partner-sign-out
          onClick={() => {
            setPartnerAuthToken(null)
            window.location.assign('/partner')
          }}
          className="shrink-0 px-2 py-1 rounded-lg border border-border text-[11px] font-semibold"
        >
          <T>Odhlásiť</T>
        </button>
      </div>

      {/* Legacy's own control - two date inputs and two quick ranges (`PartnerPortal.tsx:216`). Ported rather than
          invented, because a blank range puts the 400 back. */}
      <div className="flex flex-wrap items-center gap-2" data-partner-range>
        <input
          type="date"
          value={range.from}
          onChange={(event) => setRange((current) => ({ ...current, from: event.target.value }))}
          aria-label={tt('Od')}
          data-partner-range-from
          className="px-2 py-1 rounded-lg border border-border bg-card text-xs"
        />
        <input
          type="date"
          value={range.to}
          onChange={(event) => setRange((current) => ({ ...current, to: event.target.value }))}
          aria-label={tt('Do')}
          data-partner-range-to
          className="px-2 py-1 rounded-lg border border-border bg-card text-xs"
        />
        <button
          type="button"
          onClick={() => setRange(yearToDate(new Date()))}
          data-partner-range-ytd
          className="px-2 py-1 rounded-lg border border-border text-[11px] font-semibold"
        >
          <T>Od začiatku roka</T>
        </button>
        <button
          type="button"
          onClick={() => setRange(monthToDate(new Date()))}
          data-partner-range-month
          className="px-2 py-1 rounded-lg border border-border text-[11px] font-semibold"
        >
          <T>Tento mesiac</T>
        </button>
        {/* FB-960, §3.186 - legacy's third range, and the one the payout is about: the billing run bills a
            **completed** month, so this is the period a partner is actually paid for. The two above both end
            today, inside a month that has not finished. */}
        <button
          type="button"
          onClick={() => setRange(lastMonth(new Date()))}
          data-partner-range-last-month
          className="px-2 py-1 rounded-lg border border-border text-[11px] font-semibold"
        >
          <T>Minulý mesiac</T>
        </button>
      </div>

      <div className="rounded-2xl border border-border p-3" data-partner-totals>
        <p className="text-xs text-muted-foreground">
          <T>Váš podiel za obdobie</T>
        </p>
        {/* Withheld rather than corrected downward: this figure has no empty state to gate and no list beside it,
            so the only honest alternatives are the number or a sentence saying we could not read it. */}
        {money.isError ? (
          <p className="text-sm font-semibold text-red-500" data-partner-payout-unreadable>
            <T>Nepodarilo sa načítať - číslo nižšie by nebolo skutočné</T>
          </p>
        ) : (
          <>
            <p className="text-lg font-bold" data-partner-payout>
              {formatCents(report.totalPayoutCents)}
            </p>
            <p className="text-[11px] text-muted-foreground" data-partner-revenue>
              <T>z fakturovaných</T> {formatCents(report.totalRevenueCents)}
            </p>
            {/* §3.122's shape on this same screen: the server has always echoed `from` and `to`, the parser has
                always read them, and nothing rendered them - so the label named a period nobody could see. Off the
                echo rather than off the inputs, so it says what the figure actually covers. */}
            {periodNote(report) !== '' && (
              <p className="text-[11px] text-muted-foreground" data-partner-period>
                {periodNote(report)}
              </p>
            )}
          </>
        )}
        {money.isError ? (
          <button
            type="button"
            onClick={() => void money.refetch()}
            data-partner-payout-retry
            className="mt-2 px-2 py-0.5 rounded-lg border border-border text-[11px] font-semibold"
          >
            <T>Skúsiť znova</T>
          </button>
        ) : null}
      </div>

      {inactiveNote(franchises) !== '' && (
        <p className="text-[11px] text-amber-500" data-partner-inactive>
          {inactiveNote(franchises)}
        </p>
      )}

      {/* §3.122 - **what the partner holds**, which this page could count and never name. The payout
        * cards below are per *period* and come from `/me/profitability`, whose report selects
        * `WHERE is_active = true` - so a deactivated franchise appeared nowhere but in the note above,
        * as a digit. Legacy has this table with the same four facts, above its payout table, and the
        * two stay separate for the reason legacy keeps them apart: an assignment is not a period.
        *
        * In the order the server sends them (`ORDER BY f.id`). Sorting the inactive ones down would be
        * a second opinion about what matters, and the row says which it is. */}
      {/* FB-823, §3.152 - `franchises.length > 0` means this block is **absent** on a failure, and §3.122 built it
          precisely because a deactivated franchise otherwise appeared nowhere but as a digit in the note above. */}
      {links.isError ? (
        <p className="text-[11px] text-red-500" data-partner-holdings-unreadable>
          <T>Zoznam vašich franšíz sa nepodarilo načítať.</T>
        </p>
      ) : null}
      {franchises.length > 0 && (
        <div className="rounded-2xl border border-border p-3 space-y-2" data-partner-holdings>
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <T>Vaše franšízy</T>
          </p>
          {franchises.map((franchise) => (
            <div
              key={franchise.franchiseId}
              className="flex flex-wrap items-baseline justify-between gap-2 text-xs"
              data-partner-holding={String(franchise.franchiseId)}
              data-partner-holding-active={franchise.isActive ? 'yes' : 'no'}
            >
              <span className={franchise.isActive ? 'font-semibold' : 'font-semibold text-muted-foreground'}>
                {franchise.franchiseName}
              </span>
              <span className="text-[11px] text-muted-foreground" data-partner-holding-package>
                {packageNote(franchise)}
              </span>
              <span className="tabular-nums" data-partner-holding-share>
                {holdingShareNote(franchise)}
              </span>
              {!franchise.isActive && (
                <span className="text-[11px] text-amber-500" data-partner-holding-inactive>
                  <T>Neaktívna</T>
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {report.franchises.length === 0 && (
        <p className="text-xs text-muted-foreground" data-partner-empty>
          {emptyNote()}
        </p>
      )}

      {report.franchises.map((payout) => (
        <div
          key={payout.franchiseId}
          className="rounded-xl border border-border p-2 text-xs"
          data-partner-franchise={String(payout.franchiseId)}
          data-partner-charge={isCharge(payout) ? 'yes' : 'no'}
        >
          <p className="font-semibold">{payout.franchiseName}</p>
          {/* §3.68 - the basis above the share, so the card reads as arithmetic a partner can
            * check: what came in less what it cost, then their percentage of the difference. */}
          <p className="text-[11px] text-muted-foreground" data-partner-basis>
            {basisNote(payout)}
          </p>
          <p className="text-[11px] text-muted-foreground">{shareNote(payout)}</p>
          <p className={`text-[11px] ${isCharge(payout) ? 'text-red-500' : 'text-emerald-500'}`} data-partner-amount>
            {formatCents(payout.payoutCents)}
            {payoutNote(payout) !== '' && <span data-partner-charge-note> - {payoutNote(payout)}</span>}
          </p>
        </div>
      ))}

      {/* §3.40 - what the platform billed these franchises, which is a cost against the profit the
        * partner takes a share of. Hidden when there are none, as legacy's is: a partner with no
        * invoices has nothing to read, and an empty table says less than no table. */}
      {/* §3.40's comment says an empty table says less than no table - true of a partner with no invoices, and not
          of a read that failed. Their two partners had 6 invoices worth 3 434,16 EUR they could not see. */}
      {billing.isError ? (
        <p className="text-[11px] text-red-500" data-partner-invoices-unreadable>
          <T>Faktúry franšíz sa nepodarilo načítať.</T>
        </p>
      ) : null}
      {shown.length > 0 && (
        <div className="rounded-2xl border border-border p-3 space-y-2" data-partner-invoices>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <T>Faktúry franšíz</T>
            </p>
            {outstandingCents(invoices, now) > 0 && (
              <p className="text-[11px] text-amber-500" data-partner-outstanding>
                <T>Neuhradené</T>: {formatCents(outstandingCents(invoices, now))}
              </p>
            )}
          </div>
          {/* §3.100 - the product's table, and the *Stav* column's badge. It was a word coloured
            * by a nested ternary, which is the shape §3.96 named as the real gap and its own ratchet
            * could not see. Sorting matters here more than on most of these lists: what a partner
            * wants from it is *which of these is late*, and the outstanding total above says only
            * that some are. */}
          <DataTable
            columns={invoiceColumns(now)}
            data={shown}
            rowKey={(invoice) => invoice.id}
            rowAttributes={(invoice) => ({
              // Kept exactly: `tower.spec.ts` locates rows by both, and losing them would make this
              // a rewrite rather than a conversion.
              'data-partner-invoice': String(invoice.id),
              'data-partner-invoice-standing': invoiceStandingOf(invoice, now),
            })}
            // The list is capped at 50 by `partnerInvoicesNewestFirst`, so one page is all of it and
            // a pager under a complete list is a control that does nothing.
            pageSize={Math.max(shown.length, 1)}
            compact
            className="text-xs"
          />
        </div>
      )}

      <p className="text-[11px] text-muted-foreground" data-partner-share-count>
        {formatCount(franchises.length)} <T>franšíz</T>
      </p>
    </main>
  )
}

export function PartnerPortalPage() {
  /* FB-835, §3.159 - the session gate, where `retry: false` showed a signed-in partner the login form on one dropped
     packet. A 401 stays final by the rule. */
  const session = useGetPartnerAuthMe({ query: { retry: retryTransient } })
  const login = usePostPartnerAuthLogin()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [problem, setProblem] = useState('')

  async function submit() {
    setProblem('')
    try {
      const answer = await login.mutateAsync({ data: { email: email.trim(), password } })
      const token = (answer as { token?: unknown } | null)?.token
      if (typeof token !== 'string' || token.trim() === '') {
        setProblem(tt('Prihlásenie zlyhalo'))
        return
      }
      setPartnerAuthToken(token)
      window.location.assign('/partner')
    } catch (failure) {
      // §9.360 - this read `failure.body?.error`, and `ApiError` puts the envelope in `data`. A
      // partner whose account is suspended was told only *"Prihlásenie zlyhalo"*.
      setProblem(apiErrorMessage(failure, tt('Prihlásenie zlyhalo')))
    }
  }

  if (session.isLoading) {
    return <main className="min-h-screen" data-partner-loading />
  }
  if (!session.isError) return <PartnerDashboard />

  return (
    <main className="min-h-screen flex items-center justify-center p-4" data-partner-portal>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
        className="w-full max-w-sm space-y-3 rounded-2xl border border-border bg-card p-6"
      >
        <h1 className="text-lg font-bold">
          <T>Partnerský portál</T>
        </h1>
        <input
          data-field="email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={tt('E-mail')}
          className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
        />
        <input
          data-field="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder={tt('Heslo')}
          className="w-full min-h-[44px] rounded-xl border border-border bg-background px-3 text-sm"
        />
        <button
          type="submit"
          data-submit
          disabled={email.trim() === '' || password === '' || login.isPending}
          className="w-full min-h-[44px] rounded-xl bg-primary text-primary-foreground text-sm font-bold disabled:opacity-50"
        >
          <T>Prihlásiť sa</T>
        </button>
        {problem !== '' && (
          <p className="text-xs text-red-500" data-login-problem>
            {problem}
          </p>
        )}
      </form>
    </main>
  )
}

/**
 * The invoice columns - §3.100.
 *
 * `now` is a parameter rather than read inside, because the standing depends on it and a column
 * array that computes its own clock re-sorts under the reader.
 */
function invoiceColumns(now: Date): Column<PartnerInvoice>[] {
  return [
    {
      key: 'number',
      header: tt('Číslo'),
      className: 'font-mono',
      sortable: true,
      sortValue: (invoice) => invoice.invoiceNumber,
      render: (invoice) => (invoice.invoiceNumber === '' ? '-' : invoice.invoiceNumber),
    },
    {
      key: 'franchise',
      header: tt('Franšíza'),
      sortable: true,
      sortValue: (invoice) => invoice.franchiseName,
      render: (invoice) => invoice.franchiseName,
    },
    {
      key: 'issued',
      header: tt('Vydaná'),
      className: 'text-muted-foreground',
      sortable: true,
      sortValue: (invoice) => invoice.issuedAt,
      render: (invoice) => (invoice.issuedAt === '' ? '-' : formatDay(invoice.issuedAt)),
    },
    {
      key: 'standing',
      header: tt('Stav'),
      sortable: true,
      // **Sorted so the late ones come first**, not alphabetically by their Slovak label: the
      // question this column answers is *which of these do I need to chase*.
      sortValue: (invoice) => {
        const standing = invoiceStandingOf(invoice, now)
        return standing === 'overdue' ? 0 : standing === 'open' ? 1 : 2
      },
      render: (invoice) => {
        const standing = invoiceStandingOf(invoice, now)
        return (
          <StatusBadge
            label={invoiceStandingLabel(standing, invoice.status)}
            color={invoiceStandingColor(standing)}
          />
        )
      },
    },
    {
      key: 'amount',
      header: tt('Suma'),
      className: 'text-right tabular-nums font-bold',
      headerClassName: 'text-right',
      sortable: true,
      sortValue: (invoice) => invoice.amountCents,
      render: (invoice) => formatCents(invoice.amountCents),
    },
  ]
}
